import { useState, useEffect } from "react";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Plus, 
  Search, 
  Image as ImageIcon, 
  Video, 
  Trash2,
  MoreVertical,
  Filter,
  Grid,
  List,
  Link as LinkIcon,
  Copy,
  Check,
  X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MediaLibraryPage = ({ user, logout, token }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newMedia, setNewMedia] = useState({ url: "", filename: "", file_type: "image", tags: "" });
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, [token, filter]);

  const fetchMedia = async () => {
    try {
      let url = `${BACKEND_URL}/api/media`;
      if (filter !== "all") {
        url += `?file_type=${filter}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(response.data);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMedia = async () => {
    if (!newMedia.url.trim() || !newMedia.filename.trim()) {
      toast.error("URL y nombre son requeridos");
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/media`,
        {
          url: newMedia.url,
          filename: newMedia.filename,
          file_type: newMedia.file_type,
          tags: newMedia.tags.split(',').map(t => t.trim()).filter(t => t)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Media añadido");
      setAddDialogOpen(false);
      setNewMedia({ url: "", filename: "", file_type: "image", tags: "" });
      fetchMedia();
    } catch (error) {
      toast.error("Error al añadir media");
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Media eliminado");
      setSelectedMedia(null);
      fetchMedia();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("URL copiada");
  };

  const filteredMedia = media.filter(item => 
    item.filename.toLowerCase().includes(search.toLowerCase()) ||
    item.tags?.some(tag => tag.toLowerCase().includes(search.toLowerCase()))
  );

  // Sample images for demo
  const sampleImages = [
    {
      url: "https://images.pexels.com/photos/18394808/pexels-photo-18394808.jpeg?auto=compress&cs=tinysrgb&w=600",
      filename: "bakery-paris.jpg"
    },
    {
      url: "https://images.unsplash.com/photo-1751842838798-5b146aa49808?w=600",
      filename: "lifestyle-travel.jpg"
    },
    {
      url: "https://images.pexels.com/photos/4152607/pexels-photo-4152607.jpeg?auto=compress&cs=tinysrgb&w=600",
      filename: "content-creator.jpg"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Media Library</h1>
              <p className="text-muted-foreground mt-1">
                Gestiona tu banco de contenido multimedia
              </p>
            </div>
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gradient-neural glow-neural text-white" data-testid="add-media-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir Media
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Añadir Media</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">URL de la imagen/video</label>
                    <Input 
                      placeholder="https://..."
                      value={newMedia.url}
                      onChange={(e) => setNewMedia({ ...newMedia, url: e.target.value })}
                      data-testid="media-url-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Nombre del archivo</label>
                    <Input 
                      placeholder="mi-imagen.jpg"
                      value={newMedia.filename}
                      onChange={(e) => setNewMedia({ ...newMedia, filename: e.target.value })}
                      data-testid="media-filename-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Tipo</label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="type" 
                          value="image"
                          checked={newMedia.file_type === "image"}
                          onChange={() => setNewMedia({ ...newMedia, file_type: "image" })}
                        />
                        <ImageIcon className="w-4 h-4" />
                        Imagen
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="type" 
                          value="video"
                          checked={newMedia.file_type === "video"}
                          onChange={() => setNewMedia({ ...newMedia, file_type: "video" })}
                        />
                        <Video className="w-4 h-4" />
                        Video
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Tags (separados por coma)</label>
                    <Input 
                      placeholder="lifestyle, travel, food"
                      value={newMedia.tags}
                      onChange={(e) => setNewMedia({ ...newMedia, tags: e.target.value })}
                      data-testid="media-tags-input"
                    />
                  </div>

                  {/* Quick Add Samples */}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">O añade una imagen de ejemplo:</label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {sampleImages.map((sample, i) => (
                        <img 
                          key={i}
                          src={sample.url}
                          alt={sample.filename}
                          className="w-full h-20 object-cover rounded cursor-pointer hover:ring-2 ring-primary transition-all"
                          onClick={() => setNewMedia({ 
                            ...newMedia, 
                            url: sample.url, 
                            filename: sample.filename 
                          })}
                        />
                      ))}
                    </div>
                  </div>

                  <Button 
                    className="w-full" 
                    onClick={handleAddMedia}
                    data-testid="save-media-btn"
                  >
                    Añadir
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    placeholder="Buscar por nombre o tags..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-media-input"
                  />
                </div>
                <div className="flex gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" data-testid="filter-btn">
                        <Filter className="w-4 h-4 mr-2" />
                        {filter === 'all' ? 'Todos' : filter === 'image' ? 'Imágenes' : 'Videos'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => setFilter('all')}>Todos</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setFilter('image')}>Imágenes</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setFilter('video')}>Videos</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                    data-testid="view-mode-btn"
                  >
                    {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Media Grid/List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredMedia.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <ImageIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No hay media aún</h3>
                <p className="text-muted-foreground mb-6">
                  Añade imágenes y videos a tu biblioteca
                </p>
                <Button onClick={() => setAddDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir Media
                </Button>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
              {filteredMedia.map((item) => (
                <Card 
                  key={item.media_id}
                  className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow group"
                  onClick={() => setSelectedMedia(item)}
                  data-testid={`media-item-${item.media_id}`}
                >
                  <div className="aspect-square relative">
                    {item.file_type === 'image' ? (
                      <img 
                        src={item.url} 
                        alt={item.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-secondary flex items-center justify-center">
                        <Video className="w-12 h-12 text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button size="sm" variant="secondary">
                        Ver detalles
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <p className="text-sm font-medium truncate">{item.filename}</p>
                    {item.tags?.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {item.tags.slice(0, 2).map((tag, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card data-testid="media-list">
              <CardContent className="p-0">
                <div className="divide-y">
                  {filteredMedia.map((item) => (
                    <div 
                      key={item.media_id}
                      className="flex items-center gap-4 p-4 hover:bg-secondary/30 cursor-pointer"
                      onClick={() => setSelectedMedia(item)}
                    >
                      <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0">
                        {item.file_type === 'image' ? (
                          <img 
                            src={item.url} 
                            alt={item.filename}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-secondary flex items-center justify-center">
                            <Video className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.filename}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(item.created_at), "PPP", { locale: es })}
                        </p>
                        {item.tags?.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {item.tags.map((tag, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => copyUrl(item.url)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copiar URL
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteMedia(item.media_id)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Media Detail Dialog */}
          <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{selectedMedia?.filename}</DialogTitle>
              </DialogHeader>
              {selectedMedia && (
                <div className="space-y-4">
                  <div className="aspect-video rounded overflow-hidden bg-secondary">
                    {selectedMedia.file_type === 'image' ? (
                      <img 
                        src={selectedMedia.url} 
                        alt={selectedMedia.filename}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <video 
                        src={selectedMedia.url} 
                        controls
                        className="w-full h-full"
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <LinkIcon className="w-4 h-4 text-muted-foreground" />
                      <Input 
                        value={selectedMedia.url}
                        readOnly
                        className="flex-1 text-sm"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyUrl(selectedMedia.url)}
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>

                    {selectedMedia.tags?.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {selectedMedia.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary">{tag}</Badge>
                        ))}
                      </div>
                    )}

                    <p className="text-sm text-muted-foreground">
                      Añadido el {format(new Date(selectedMedia.created_at), "PPP", { locale: es })}
                    </p>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => handleDeleteMedia(selectedMedia.media_id)}
                      className="text-destructive"
                      data-testid="delete-media-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};
